/**
 * CodigoViewer - Visualizador de códigos de ética
 * Features:
 * - Vista completa del código
 * - Secciones estructuradas
 * - Export a PDF
 * - Compartir
 */
import { useState, useEffect } from 'react';
import { Download, Share2, FileText, Calendar, User } from 'lucide-react';
import { codigosAPI } from '../../api';
import { Button, Loading, Alert } from '../common';

const CodigoViewer = ({ codigoId }) => {
  const [codigo, setCodigo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCodigo();
  }, [codigoId]);

  const loadCodigo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await codigosAPI.getById(codigoId);
      setCodigo(response.data);
    } catch (err) {
      console.error('Error al cargar código:', err);
      setError(err.message || 'Error al cargar código');
    } finally {
      setLoading(false);
    }
  };

  const handleDescargar = () => {
    alert('Función de descarga PDF en desarrollo');
  };

  const handleCompartir = () => {
    alert('Función de compartir en desarrollo');
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando código..." />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" title="Error" message={error} />;
  }

  if (!codigo) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No se pudo cargar el código</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-primary-900">{codigo.nombre}</h2>
            <p className="text-primary-700 mt-1">Versión {codigo.version}</p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              codigo.estado === 'activo'
                ? 'bg-green-100 text-green-800'
                : codigo.estado === 'borrador'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {codigo.estado.charAt(0).toUpperCase() + codigo.estado.slice(1)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-primary-800">
            <Calendar className="h-4 w-4" />
            <span>
              Generado: {new Date(codigo.fecha_generacion).toLocaleDateString('es-ES')}
            </span>
          </div>
          {codigo.aprobado_por && (
            <div className="flex items-center gap-2 text-primary-800">
              <User className="h-4 w-4" />
              <span>Aprobado por comité</span>
            </div>
          )}
        </div>
      </div>

      {/* Contenido del código */}
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="prose prose-sm max-w-none">
          {codigo.contenido_json?.secciones?.map((seccion, index) => (
            <div key={index} className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-primary-200">
                {seccion.titulo}
              </h2>
              <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                {seccion.contenido}
              </div>
            </div>
          ))}

          {!codigo.contenido_json?.secciones && (
            <div className="text-gray-700 whitespace-pre-line">
              {codigo.contenido_texto || 'Contenido no disponible'}
            </div>
          )}
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button variant="outline" onClick={handleCompartir} icon={Share2}>
          Compartir
        </Button>
        <Button variant="outline" onClick={handleDescargar} icon={Download}>
          Descargar PDF
        </Button>
        <Button variant="primary" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>
    </div>
  );
};

export default CodigoViewer;
